import { useState } from 'react'
import { HelpCircle, ClipboardList, CalendarPlus, Play, QrCode, Award, ChevronRight } from 'lucide-react'

import { Button } from '@/ui/button'
import { Modal } from '@/ui/modal'
import { cn } from '@/core/lib/utils'

interface Step {
  num: string
  icon: React.ElementType
  title: string
  desc: string
  color: string
}

const steps: Step[] = [
  {
    num: '1',
    icon: CalendarPlus,
    title: 'Schedule class',
    desc: 'Configure the session on the calendar with date, location, and spots.',
    color: 'from-blue-500 to-blue-600',
  },
  {
    num: '2',
    icon: Play,
    title: 'Start class',
    desc: 'When students arrive, click Start. You are automatically assigned as instructor.',
    color: 'from-emerald-500 to-emerald-600',
  },
  {
    num: '3',
    icon: QrCode,
    title: 'QR Scan',
    desc: 'Show the QR code so students can register from their phone.',
    color: 'from-amber-500 to-amber-600',
  },
  {
    num: '4',
    icon: Award,
    title: 'End and certify',
    desc: 'Click End — certificates are automatically generated for those who completed.',
    color: 'from-primary to-primary-deep',
  },
]

export function HelpButton() {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <HelpCircle className="w-4 h-4" />
        Help
      </Button>

      <Modal open={open} onClose={() => setOpen(false)} showCloseButton className="max-w-2xl">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-navy to-navy-light flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-base font-bold text-navy">How to teach a class</p>
            <p className="text-xs text-slate-500">Follow these 4 steps</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {steps.map((step, i) => {
            const Icon = step.icon
            return (
              <div
                key={step.num}
                className="relative bg-slate-50 border border-slate-150 rounded-xl p-4"
              >
                {i < steps.length - 1 && (
                  <div className="hidden sm:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10">
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-1.5 shrink-0">
                    <div className={cn('w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center shadow-sm', step.color)}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Step {step.num}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-navy mb-0.5">{step.title}</p>
                    <p className="text-xs text-slate-500 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </Modal>
    </>
  )
}
